/**
 * 热门目的地配置
 * 用于首页"去哪里玩"区域展示
 * 
 * 注意：
 * - Web 端使用本地图片路径 (如 /assets/images/destinations/tokyo.jpg)
 * - Mobile 端使用完整的 URL 指向同一资源
 */

/** Web 端基础 URL */
const WEB_BASE_URL = 'https://travel.cubemeet.app';

export interface HotDestinationConfig {
  /** 唯一标识符 */
  key: string;
  /** 城市名称的 i18n key */
  labelKey: string;
  /** 默认显示名称 */
  defaultLabel: string;
  /** 图片相对路径 */
  imagePath: string;
  /** 导航路径 (Web 端用) */
  webPath: string;
  /** 导航参数 (Mobile 端用) */
  mobileParams: {
    screen: string;
    params: Record<string, string>;
  };
}

/** 获取 Web 端图片 URL */
export const getWebImageUrl = (config: HotDestinationConfig): string => config.imagePath;

/** 获取 Mobile 端图片 URL */
export const getMobileImageUrl = (config: HotDestinationConfig): string => `${WEB_BASE_URL}${config.imagePath}`;

/**
 * 热门目的地列表
 * 可以在这里添加、删除或调整顺序
 */
export const hotDestinationsConfig: HotDestinationConfig[] = [
  {
    key: 'tokyo',
    labelKey: 'destinations.cities.Tokyo',
    defaultLabel: '东京',
    imagePath: '/assets/images/destinations/tokyo.jpg',
    webPath: '/city/tokyo',
    mobileParams: {
      screen: 'CityDetail',
      params: { cityKey: 'tokyo' },
    },
  },
  {
    key: 'osaka',
    labelKey: 'destinations.cities.Osaka',
    defaultLabel: '大阪',
    imagePath: '/assets/images/destinations/osaka.jpg',
    webPath: '/city/osaka',
    mobileParams: {
      screen: 'CityDetail',
      params: { cityKey: 'osaka' },
    },
  },
  {
    key: 'kyoto',
    labelKey: 'destinations.cities.Kyoto',
    defaultLabel: '京都',
    imagePath: '/assets/images/destinations/kyoto.jpg',
    webPath: '/city/kyoto',
    mobileParams: {
      screen: 'CityDetail',
      params: { cityKey: 'kyoto' },
    },
  },
  {
    key: 'taipei',
    labelKey: 'destinations.cities.Taipei',
    defaultLabel: '台北',
    imagePath: '/assets/images/destinations/taibei.jpg',
    webPath: '/city/taipei',
    mobileParams: {
      screen: 'CityDetail',
      params: { cityKey: 'taipei' },
    },
  },
  {
    key: 'hongkong',
    labelKey: 'destinations.cities.HongKong',
    defaultLabel: '香港',
    imagePath: '/assets/images/destinations/hongkong.jpg',
    webPath: '/city/hongkong',
    mobileParams: {
      screen: 'CityDetail',
      params: { cityKey: 'hongkong' },
    },
  },
  {
    key: 'beijing',
    labelKey: 'destinations.cities.Beijing',
    defaultLabel: '北京',
    imagePath: '/assets/images/destinations/beijing.jpg',
    webPath: '/city/beijing',
    mobileParams: {
      screen: 'CityDetail',
      params: { cityKey: 'beijing' },
    },
  },
  {
    key: 'shanghai',
    labelKey: 'destinations.cities.Shanghai',
    defaultLabel: '上海',
    imagePath: '/assets/images/destinations/shanghai.jpg',
    webPath: '/city/shanghai',
    mobileParams: {
      screen: 'CityDetail',
      params: { cityKey: 'shanghai' },
    },
  },
  {
    key: 'macau',
    labelKey: 'destinations.cities.Macau',
    defaultLabel: '澳門',
    imagePath: '/assets/images/destinations/macau.jpg',
    webPath: '/city/macau',
    mobileParams: {
      screen: 'CityDetail',
      params: { cityKey: 'macau' },
    },
  },
  {
    key: 'seoul',
    labelKey: 'destinations.cities.Seoul',
    defaultLabel: '首尔',
    imagePath: '/assets/images/destinations/seoul.jpg',
    webPath: '/city/seoul',
    mobileParams: {
      screen: 'CityDetail',
      params: { cityKey: 'seoul' },
    },
  },
  {
    key: 'bangkok',
    labelKey: 'destinations.cities.Bangkok',
    defaultLabel: '曼谷',
    imagePath: '/assets/images/destinations/bangkok.jpg',
    webPath: '/city/bangkok',
    mobileParams: {
      screen: 'CityDetail',
      params: { cityKey: 'bangkok' },
    },
  },
  {
    key: 'singapore',
    labelKey: 'destinations.cities.Singapore',
    defaultLabel: '新加坡',
    imagePath: '/assets/images/destinations/singapore.jpg',
    webPath: '/city/singapore',
    mobileParams: {
      screen: 'CityDetail',
      params: { cityKey: 'singapore' },
    },
  },
  {
    key: 'kuala-lumpur',
    labelKey: 'destinations.cities.KualaLumpur',
    defaultLabel: '吉隆坡',
    imagePath: '/assets/images/destinations/Kuala-Lumpur.jpg',
    webPath: '/city/kuala-lumpur',
    mobileParams: {
      screen: 'CityDetail',
      params: { cityKey: 'kuala-lumpur' },
    },
  },
];

/**
 * 获取首页显示的热门目的地（前 N 个）
 * 返回带有平台特定图片 URL 的配置
 */
export interface HotDestinationWithUrls extends HotDestinationConfig {
  webImageUrl: string;
  mobileImageUrl: string;
}

export const getHomeHotDestinations = (count: number = 4): HotDestinationWithUrls[] => {
  return hotDestinationsConfig.slice(0, count).map(config => ({
    ...config,
    webImageUrl: getWebImageUrl(config),
    mobileImageUrl: getMobileImageUrl(config),
  }));
};

export default hotDestinationsConfig;
